"use client";

import { cn } from "@/utils/cn";
import { useState } from "react";
import { Label, Text } from "react-aria-components";

interface ButtonGroupProps<T = unknown> {
	label?: string;
	labelClassName?: string;
	options: { label: string; value: T }[];
	value: T | T[];
	onChange: (value: T[] | T | null) => void;
	multiple?: boolean;
	expandable?: boolean;
	initialCount?: number;
	increment?: number;
	description?: string;
	errorMessage?: string;
	className?: string;
}

export const ButtonGroup = <T = unknown,>({
	label,
	labelClassName,
	options,
	value,
	onChange,
	multiple = false,
	expandable = false,
	initialCount = 3,
	increment = 3,
	description,
	errorMessage,
	className,
}: ButtonGroupProps<T>) => {
	const [visibleCount, setVisibleCount] = useState(initialCount);
	const visibleOptions = expandable ? options.slice(0, visibleCount) : options;
	const hasMore = expandable && visibleCount < options.length;

	const handleShowMore = () => setVisibleCount(c => c + increment);

	const isSelected = (v: T) => (multiple ? Array.isArray(value) && value.includes(v) : value === v);

	const toggleValue = (v: T) => {
		let newValue;
		if (multiple) {
			if (Array.isArray(value)) {
				newValue = isSelected(v) ? value.filter(x => x !== v) : [...(Array.isArray(value) ? value : []), v];
			} else {
				newValue = [v];
			}
		} else {
			newValue = isSelected(v) ? null : v;
		}
		onChange(newValue);
	};

	return (
		<div className="group flex flex-col py-1 my-1 gap-1.5">
			{label && <Label className={cn("text-title-medium", labelClassName)}>{label}</Label>}

			<div className={cn("flex flex-wrap items-center", className ?? "")}>
				{visibleOptions.map((o, index) => (
					<button
						type="button"
						key={`${index}-${String(o.value)}`}
						onClick={() => toggleValue(o.value)}
						className={cn(
							"px-4 py-3 text-body shadow-sm hover:bg-neutral-300 cursor-pointer",
							isSelected(o.value) ? "bg-primary-400" : "bg-white",
						)}
					>
						{o.label}
					</button>
				))}

				{hasMore && (
					<button
						type="button"
						onClick={handleShowMore}
						className={"px-4 py-3 text-body shadow-sm hover:bg-neutral-300 cursor-pointer"}
					>
						Show More
					</button>
				)}
			</div>

			{description && (
				<Text slot="description" className="text-xs text-muted-foreground">
					{description}
				</Text>
			)}

			{errorMessage && (
				<Text slot="errorMessage" className="text-sm font-medium text-destructive">
					{errorMessage}
				</Text>
			)}
		</div>
	);
};
